import { useEffect } from "react";
import { shallow } from "zustand/shallow";
import { useAccessControl } from "@/hooks/useAccessControl";
import useTabStore from "@/stores/useTabStore";
import { useOnboardingStore } from "./useOnboardingStore";
// Re-exported so existing importers of this module keep working; the rule
// itself lives in eligibility.ts so it is testable without pulling in the app.
import { isOnboardingEligible, readOnboardingEnabledClaim } from "./eligibility";
import { getOnboardingOverride } from "./override";
import { railVisibility } from "./rail/railVisibility";
import { useGuideViewport } from "@/shared/hooks/useGuideViewport";
import useAuth from "@/hooks/useAuth";

export { isOnboardingEligible };

export type OnboardingGatePhase =
  /** Async sources (tabs, subscription, onboarding pref) still settling. */
  | "waiting"
  /** Not the target audience — never show onboarding. */
  | "ineligible"
  /** First ever login: show the welcome flow from the start. */
  | "trigger"
  /** Flow started but not finished/dismissed: show it at lastStep. */
  | "resume"
  /** Completed, dismissed, or an established user. */
  | "done";

/**
 * Derives the onboarding trigger state. Pure derivation — rendering decisions
 * live in OnboardingGate. Also kicks off the onboarding-state fetch once
 * authenticated.
 */
export function useOnboardingGate(): {
  phase: OnboardingGatePhase;
  /** The guided run. Supersedes the checklist — never both. */
  showRail: boolean;
  /** What's left over, plus the way back into a run that was closed. */
  showChecklist: boolean;
  /**
   * Live product access — decides whether the rail carries the two steps that
   * reach the desk (squawk, analyst chat). See visibleRailSteps for why this is
   * checked when onboarding already requires a trial.
   */
  hasLiveAccess: boolean;
  /** Whether the flow can run at this width — see useGuideViewport. */
  viewportSupported: boolean;
} {
  const access = useAccessControl();
  const { tokenParsed } = useAuth();
  const { tabsLoaded, serverTabsEmpty } = useTabStore(
    (s) => ({ tabsLoaded: s.tabsLoaded, serverTabsEmpty: s.serverTabsEmpty }),
    shallow
  );
  const { state, loaded, loadFailed, load } = useOnboardingStore(
    (s) => ({
      state: s.state,
      loaded: s.loaded,
      loadFailed: s.loadFailed,
      load: s.load,
    }),
    shallow
  );

  // Fetch onboarding state once the user is authenticated.
  useEffect(() => {
    if (access.authenticated) {
      load();
    }
  }, [access.authenticated, load]);

  /**
   * The override (?setup=start, or the dev params) bypasses the audience rules
   * and the empty-tabs check — never the viewport. A run it started keeps that
   * standing through `startedVia`, since the override only lasts the tab.
   */
  const force =
    getOnboardingOverride() !== null || state?.startedVia === "link";

  const viewportSupported = useGuideViewport();

  /**
   * Resolved once and shared by the phase and the rail, which each used to
   * call the rule themselves — two reads of one predicate that must agree.
   */
  const eligible = isOnboardingEligible({
    ...access,
    onboardingEnabled: readOnboardingEnabledClaim(
      tokenParsed as Record<string, unknown> | undefined
    ),
    viewportSupported,
  });

  let phase: OnboardingGatePhase;
  if (!access.authenticated) {
    phase = "ineligible";
  } else if (
    !tabsLoaded ||
    !loaded ||
    access.subscriptionQuery.isLoading
  ) {
    // Wait for ALL async sources before deciding — prevents flashing the
    // welcome modal at users who turn out to be ineligible.
    phase = "waiting";
  } else if (loadFailed || !viewportSupported || (!force && !eligible)) {
    phase = "ineligible";
  } else if (state && !state.completedAt && !state.dismissedAt) {
    phase = "resume";
  } else if (!state && (serverTabsEmpty || force)) {
    phase = "trigger";
  } else {
    // Completed/dismissed, or an established user (has tabs, no state) —
    // deliberately don't disturb users who already built a dashboard.
    phase = "done";
  }

  const { showRail, showChecklist } = railVisibility({
    phase,
    eligible: force || eligible,
    hasState: !!state,
    rail: state?.rail,
    checklistDismissedAt: state?.checklist?.dismissedAt,
  });

  return {
    phase,
    showRail,
    showChecklist,
    hasLiveAccess: access.hasLiveAccess,
    viewportSupported,
  };
}
